import React from 'react';
import {
    Box, Typography, Dialog, DialogTitle, DialogContent, IconButton,
} from '@mui/material';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import CloseIcon from '@mui/icons-material/Close';
import { PHOTO_CATEGORIES, MODEL_CATEGORIES } from './types';

const t = (key, fallback) => fallback || key;

const dialogPaperSx = {
    borderRadius: 2,
    bgcolor: '#1a1a3e',
    boxShadow: '0 8px 32px rgba(26,26,62,0.6)',
    overflow: 'visible',
};

const backdropSx = { bgcolor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' };

const cardSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 0.75,
    p: 1.5,
    borderRadius: 1.5,
    cursor: 'pointer',
    minWidth: 80,
    border: '1px solid rgba(255,255,255,0.15)',
    bgcolor: 'rgba(255,255,255,0.08)',
    transition: 'all 0.2s ease',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    userSelect: 'none',
    '@media (hover: hover)': {
        '&:hover': {
            transform: 'translateY(-4px) scale(1.05)',
            bgcolor: 'rgba(255,255,255,0.18)',
            borderColor: 'rgba(255,255,255,0.3)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
        },
    },
    '&:active': {
        bgcolor: 'rgba(255,255,255,0.18)',
        borderColor: 'rgba(255,255,255,0.3)',
    },
};

const PHOTO_EMOJI = {
    'Full Smile': '😁',
    'Profile': '👤',
    '12 O\'Clock': '🕛',
    'Others': '📁',
};

export const PhotoCategoryDialog = ({
    open, pendingImages, currentIndex, onSelect, onClose,
}) => {
    const currentFile = pendingImages[currentIndex];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
            slotProps={{ backdrop: { sx: backdropSx } }}
            PaperProps={{ sx: dialogPaperSx }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0.5 }}>
                <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600, color: '#fff' }}>
                    {t('patient.selectCategory', 'Select photo type')}
                </Typography>
                <IconButton size="small" onClick={onClose}
                    sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 0, pb: 2.5, px: 2.5 }}>
                {currentFile && (
                    <Box sx={{
                        width: '100%', height: 180, borderRadius: 1.5, overflow: 'hidden',
                        mb: 2, bgcolor: 'rgba(255,255,255,0.1)', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}>
                        <img src={URL.createObjectURL(currentFile)} alt={currentFile.name}
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </Box>
                )}
                {pendingImages.length > 1 && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1.5, display: 'block' }}>
                        {`Image ${currentIndex + 1} of ${pendingImages.length}`}
                    </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {PHOTO_CATEGORIES.map((cat) => (
                        <Box key={cat.value} role="button" tabIndex={0} onClick={() => onSelect(cat.value)} sx={cardSx}>
                            <Box sx={{
                                width: 44, height: 44, borderRadius: '50%',
                                bgcolor: 'rgba(255,255,255,0.15)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            }}>
                                <Typography sx={{ fontSize: 20 }}>{PHOTO_EMOJI[cat.value] || '📁'}</Typography>
                            </Box>
                            <Typography variant="caption" sx={{
                                fontWeight: 500, color: '#fff', fontSize: '0.7rem', textAlign: 'center', lineHeight: 1.2,
                            }}>
                                {t(`patient.category.${cat.value}`, cat.label)}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export const ModelCategoryDialog = ({
    open, pendingModels, currentIndex, onSelect, onClose,
}) => {
    const currentFile = pendingModels[currentIndex];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
            slotProps={{ backdrop: { sx: backdropSx } }}
            PaperProps={{ sx: dialogPaperSx }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0.5 }}>
                <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600, color: '#fff' }}>
                    {t('patient.selectModelCategory', 'Select 3D model type')}
                </Typography>
                <IconButton size="small" onClick={onClose}
                    sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 0, pb: 2.5, px: 2.5 }}>
                {currentFile && (
                    <Box sx={{
                        width: '100%', py: 3, borderRadius: 1.5, mb: 2,
                        bgcolor: 'rgba(255,255,255,0.06)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                    }}>
                        <ViewInArIcon sx={{ fontSize: 48, color: '#c9a0dc' }} />
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
                            {currentFile.name}
                        </Typography>
                    </Box>
                )}
                {pendingModels.length > 1 && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1.5, display: 'block' }}>
                        {`File ${currentIndex + 1} of ${pendingModels.length}`}
                    </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {MODEL_CATEGORIES.map((cat) => (
                        <Box key={cat.value} role="button" tabIndex={0} onClick={() => onSelect(cat.value)} sx={cardSx}>
                            <Box sx={{
                                width: 44, height: 44, borderRadius: '50%',
                                bgcolor: cat.value.startsWith('PreOp') ? '#7b68ee' : '#c9a0dc',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: `0 2px 10px ${cat.value.startsWith('PreOp') ? 'rgba(123,104,238,0.4)' : 'rgba(201,160,220,0.4)'}`,
                            }}>
                                <ViewInArIcon sx={{ fontSize: 22, color: '#fff' }} />
                            </Box>
                            <Typography variant="caption" sx={{
                                fontWeight: 500, color: '#fff', fontSize: '0.7rem', textAlign: 'center', lineHeight: 1.2,
                            }}>
                                {t(`patient.modelCategory.${cat.value}`, cat.label)}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </DialogContent>
        </Dialog>
    );
};
